import React, { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { loadPdfjsDoc, fileToUint8, renderPage } from "@/lib/pdfEditor";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Loader2, UploadCloud, Scissors, FileText } from "lucide-react";

const ASPECTS = [
  { key: "1:1", value: 1 },
  { key: "4:3", value: 4 / 3 },
  { key: "3:4", value: 3 / 4 },
  { key: "16:9", value: 16 / 9 },
  { key: "3:1", value: 3 },
];

async function cropToPng(imageSrc, areaPx, circle = false) {
  const img = new Image();
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = imageSrc; });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(areaPx.width);
  canvas.height = Math.round(areaPx.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, areaPx.x, areaPx.y, areaPx.width, areaPx.height, 0, 0, canvas.width, canvas.height);
  if (circle) {
    ctx.globalCompositeOperation = "destination-in";
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, Math.min(canvas.width, canvas.height) / 2, 0, Math.PI * 2);
    ctx.fill();
  }
  return canvas.toDataURL("image/png");
}

export const CropPdfDialog = ({ open, onClose, onInsert, labels }) => {
  const inputRef = useRef(null);
  const docRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [pageIdx, setPageIdx] = useState(0);
  const [pageImg, setPageImg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(4 / 3);
  const [shape, setShape] = useState("rect");
  const [areaPx, setAreaPx] = useState(null);

  const reset = () => {
    docRef.current = null;
    setNumPages(0); setPageIdx(0); setPageImg(null);
    setCrop({ x: 0, y: 0 }); setZoom(1); setAreaPx(null);
    setShape("rect"); setAspect(4 / 3);
  };

  const loadPage = async (doc, idx) => {
    setLoading(true);
    try {
      // High-resolution render (scale 2.5 ≈ 180 DPI) so the inserted PNG stays sharp
      const r = await renderPage(doc, idx, 2.5);
      setPageImg(r.dataUrl);
      setCrop({ x: 0, y: 0 }); setZoom(1); setAreaPx(null);
    } catch (e) {
      console.error(e);
      toast.error(labels.err);
    } finally {
      setLoading(false);
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const doc = await loadPdfjsDoc(await fileToUint8(file));
      docRef.current = doc;
      setNumPages(doc.numPages);
      setPageIdx(0);
      await loadPage(doc, 0);
    } catch (e) {
      console.error(e);
      toast.error(labels.err);
      setLoading(false);
    }
  };

  const goPage = async (d) => {
    const next = Math.max(0, Math.min(numPages - 1, pageIdx + d));
    if (next === pageIdx) return;
    setPageIdx(next);
    await loadPage(docRef.current, next);
  };

  const onCropComplete = useCallback((_, px) => setAreaPx(px), []);

  const insertCrop = async () => {
    if (!pageImg || !areaPx) return;
    onInsert(await cropToPng(pageImg, areaPx, shape === "circle"));
    reset(); onClose();
  };
  const insertFull = () => {
    if (!pageImg) return;
    onInsert(pageImg);
    reset(); onClose();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="crop-pdf-dialog">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
            <Scissors className="h-5 w-5 text-emerald-600" /> {labels.title}
          </h3>
          <button data-testid="crop-close" onClick={() => { reset(); onClose(); }} className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>

        <input ref={inputRef} type="file" accept=".pdf,application/pdf" className="hidden" data-testid="crop-file-input" onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />

        {!pageImg ? (
          <div
            data-testid="crop-dropzone"
            onClick={() => inputRef.current?.click()}
            className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center transition-colors hover:border-emerald-400 hover:bg-emerald-50/40"
          >
            {loading ? <Loader2 className="h-8 w-8 animate-spin text-emerald-600" /> : (
              <>
                <UploadCloud className="mb-3 h-9 w-9 text-emerald-600" />
                <p className="font-semibold text-slate-700">{labels.load}</p>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button data-testid="crop-prev-page" onClick={() => goPage(-1)} disabled={pageIdx === 0 || loading} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                <span className="text-sm font-semibold text-slate-600">{labels.page} {pageIdx + 1} / {numPages}</span>
                <button data-testid="crop-next-page" onClick={() => goPage(1)} disabled={pageIdx >= numPages - 1 || loading} className="rounded-lg border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-0.5">
                  <button data-testid="crop-shape-square" onClick={() => setShape("rect")}
                    className={`rounded-md px-2 py-1 text-xs font-bold ${shape === "rect" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-200"}`}>
                    {labels.shapeSquare}
                  </button>
                  <button data-testid="crop-shape-circle" onClick={() => setShape("circle")}
                    className={`rounded-md px-2 py-1 text-xs font-bold ${shape === "circle" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-200"}`}>
                    {labels.shapeCircle}
                  </button>
                </div>
                {shape === "rect" && (
                  <div className="flex items-center gap-1">
                    {ASPECTS.map((a) => (
                      <button key={a.key} data-testid={`crop-aspect-${a.key}`} onClick={() => setAspect(a.value)}
                        className={`rounded-lg px-2 py-1 text-xs font-bold ${aspect === a.value ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                        {a.key}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="relative h-[380px] w-full overflow-hidden rounded-xl bg-slate-900" data-testid="crop-area">
              {loading ? (
                <div className="flex h-full items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>
              ) : (
                <Cropper
                  image={pageImg}
                  crop={crop}
                  zoom={zoom}
                  aspect={shape === "circle" ? 1 : aspect}
                  cropShape={shape === "circle" ? "round" : "rect"}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              )}
            </div>

            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs font-semibold text-slate-500">{labels.zoom}</span>
              <input data-testid="crop-zoom" type="range" min="1" max="4" step="0.05" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="w-40 accent-emerald-600" />
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button data-testid="crop-change-file" onClick={() => inputRef.current?.click()} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">{labels.changeFile}</button>
              <button data-testid="crop-insert-full" onClick={insertFull} className="flex items-center gap-2 rounded-xl border border-emerald-500 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50"><FileText className="h-4 w-4" /> {labels.insertFull}</button>
              <button data-testid="crop-insert" onClick={insertCrop} disabled={!areaPx} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-40"><Scissors className="h-4 w-4" /> {labels.insert}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
